import React, { useEffect } from 'react';

// assets

// services

// props

// components
import { Checkbox, DefaultButton, Panel, PanelType, PrimaryButton, SearchBox, Stack } from '@fluentui/react';
import Label from '../../../typography/label';
import Text from '../../../typography/text';
import LoadingComponent from '../../../feedbacks/loading';
import { ListItemsFilterByOptionProps } from '../props';

type GenericFilterPanelProps = {
    title?: string;
    selected?: ListItemsFilterByOptionProps[];
    onRetrieveOptions?(): Promise<ListItemsFilterByOptionProps[]>;
    onApply(selected: ListItemsFilterByOptionProps[]): void;
    onCancel(): void;
}

type GroupedOptionProps = {
    key: string;
    options: ListItemsFilterByOptionProps[];
}

const GenericFilterPanel: React.FC<GenericFilterPanelProps> = (props: GenericFilterPanelProps) => {
    const [options, setOptions] = React.useState<ListItemsFilterByOptionProps[]>([]);
    const [selected, setSelected] = React.useState<ListItemsFilterByOptionProps[]>(props.selected || []);
    const [keyword, setKeyword] = React.useState<string>("");

    const [loaded, setLoaded] = React.useState<boolean>(false);

    React.useEffect(() => {
        init();
    }, []);

    const init = async () => {
        setLoaded(false);

        if (props.onRetrieveOptions) {
            const _options = await props.onRetrieveOptions();
            setOptions(_options);
        } else {
            setOptions([]);
        }

        setLoaded(true);
    }

    const renderOption = (option: ListItemsFilterByOptionProps) => {
        const isSelected = selected.find((sel) => sel.key === option.key) !== undefined;

        return <Checkbox checked={isSelected}
            onRenderLabel={() => {
                return <Stack styles={{ root: { paddingLeft: 5 } }}>
                    <Label size={'small'} style={{marginTop: 4}}>{option.title}</Label>
                    {option.secondaryText ? <Text size={'small'}>{option.secondaryText}</Text> : null}
                </Stack>
            }}
            onChange={() => {
                if (isSelected) {
                    setSelected([...selected.filter((sel) => sel.key !== option.key)]);
                } else {
                    setSelected([...selected, option]);
                }
            }} />
    }

    const filterOptions = (options: ListItemsFilterByOptionProps[], keyword: string): ListItemsFilterByOptionProps[] => {
        const key = keyword.toLowerCase();
        const filtered = options.filter((option) => {
            return option.key.toLowerCase().indexOf(key) > -1 ||
                option.title.toLowerCase().indexOf(key) > -1 ||
                (option.secondaryText || "").toLowerCase().indexOf(key) > -1 ||
                (option.group || "").toLowerCase().indexOf(key) > -1;
        });

        return filtered;
    }

    const groupOptions = (options: ListItemsFilterByOptionProps[]): GroupedOptionProps[] => {
        const grouped: GroupedOptionProps[] = [];

        options.forEach((opt) => {
            const idx = grouped.findIndex((g) => g.key === (opt.group || ""));
            if (idx > -1) {
                grouped[idx].options.push(opt);
            } else {
                grouped.push({ key: opt.group || "", options: [opt] });
            }
        });

        return grouped.sort((a, b) => ((a.key || "") > (b.key || "")) ? 1 : (((b.key || "") > (a.key || "")) ? -1 : 0));
    }

    const renderOptions = (options: ListItemsFilterByOptionProps[]) => {
        const filtered = filterOptions(options, keyword);
        const grouped = groupOptions(filtered);

        return <>
            {filtered.length > 0 && grouped.length > 0 ? <Stack>
                {grouped.map((group, idx) => {
                    return <Stack tokens={{ childrenGap: 10 }}>
                        {group.key && group.key.trim() !== "" ? <Label size={'small'} className={'color-primary'} style={{ marginTop: idx === 0 ? 0 : 10 }}>{group.key}</Label> : null}
                        {group.options.map(renderOption)}
                    </Stack>
                })}
            </Stack> : null}
            {filtered.length < 1 ? <Text style={{ fontStyle: 'italic' }}>No options found</Text> : null}
        </>
    }

    return <Panel headerText={props.title || "Filter"}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onCancel()}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                <PrimaryButton text={'Apply'} onClick={() => props.onApply(selected)} />
                <DefaultButton text={'Cancel'} onClick={() => props.onCancel()} />
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 20 }}>
            {!loaded ? <LoadingComponent label='Retrieving filter ...' labelPosition='right' spinnerPosition='baseline' /> : null}
            {loaded ? <>
                <SearchBox value={keyword} placeholder='Search ...' onChange={(evt, val) => setKeyword(val || "")} />
                <Stack tokens={{ childrenGap: 10 }}>
                    <Stack>
                        {options.length > 0 ? <Checkbox
                            onRenderLabel={() => {
                                return <Stack styles={{ root: { paddingLeft: 5 } }}>
                                    <Label size={'small'} style={{marginTop: 4}}>Select All</Label>
                                </Stack>
                            }}
                            checked={options.find((opt) => {
                                const isSelected = selected.find((sel) => sel.key === opt.key) !== undefined;
                                return !isSelected;
                            }) === undefined}
                            onChange={(ev?: any, checked?: boolean) => {
                                if (checked) {
                                    setSelected([...selected.filter((sel) => {
                                        const exist = options.find((opt) => opt.key === sel.key);
                                        return !exist;
                                    }), ...(options.map((opt) => opt))]);
                                } else {
                                    setSelected(selected.filter((sel) => {
                                        const exist = options.find((opt) => opt.key === sel.key);
                                        return !exist;
                                    }));
                                }
                            }} /> : null}
                    </Stack>
                    {renderOptions(options)}
                </Stack>
            </> : null}
        </Stack>
    </Panel>
};

export default GenericFilterPanel;
